import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ChevronDown, Target, Check, Plus, Loader2, Settings as SettingsIcon } from "lucide-react";

/**
 * Compact "which goal am I training for?" pill in the header.
 * - Shows the active goal's role_target.
 * - Clicking opens a menu of every goal on the account.
 * - Picking a different goal calls POST /goals/{id}/activate then refreshes.
 */
export default function GoalSwitcher() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [goals, setGoals] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const rootRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/goals");
      setGoals(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load, user?.active_goal_id]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const active = goals.find((g) => g.goal_id === user?.active_goal_id) || goals[0];

  const activate = async (g) => {
    if (g.goal_id === active?.goal_id) { setOpen(false); return; }
    setBusyId(g.goal_id);
    try {
      await api.post(`/goals/${g.goal_id}/activate`);
      await refreshUser();
      toast.success(`Switched to: ${g.role_target}`);
      setOpen(false);
      // reload the current page's data by navigating in-place
      window.dispatchEvent(new Event("adyap:goal-switched"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  if (!user) return null;
  if (!goals.length) {
    return (
      <button
        data-testid="goal-switcher-empty"
        onClick={() => navigate("/onboarding")}
        className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs border border-white/10 text-zinc-300 hover:border-primary/40 hover:text-white transition-colors"
      >
        <Plus className="w-3.5 h-3.5" /> Set your first goal
      </button>
    );
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        data-testid="goal-switcher-btn"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs border border-white/10 hover:border-white/25 bg-white/[0.02] text-zinc-200 max-w-[220px] transition-colors"
        title="Switch goal"
      >
        <Target className="w-3.5 h-3.5 text-primary shrink-0" />
        <span className="truncate font-medium">{active?.role_target || "Choose a goal"}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="goal-switcher-menu"
          className="absolute right-0 mt-2 w-72 rounded-xl border border-white/10 shadow-2xl overflow-hidden z-50"
          style={{ background: "rgba(12,12,12,0.98)" }}
        >
          <div className="px-3 py-2 text-[10px] tracking-wider uppercase font-semibold text-zinc-500 border-b border-white/5">
            Your goals
          </div>
          <div className="max-h-72 overflow-y-auto">
            {goals.map((g) => {
              const isActive = g.goal_id === active?.goal_id;
              const busy = busyId === g.goal_id;
              return (
                <button
                  key={g.goal_id}
                  data-testid={`switcher-goal-${g.goal_id}`}
                  onClick={() => activate(g)}
                  disabled={busy}
                  className={`w-full text-left flex items-start gap-2 px-3 py-2.5 hover:bg-white/[0.04] transition-colors ${isActive ? "bg-primary/5" : ""}`}
                >
                  <div className="mt-0.5 shrink-0 w-4">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" /> : (isActive ? <Check className="w-3.5 h-3.5 text-primary" /> : null)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{g.role_target}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {g.user_chosen_days || g.ai_recommended_days} days · <span className="capitalize">{g.status}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="border-t border-white/5 flex">
            <Link
              to="/onboarding"
              onClick={() => setOpen(false)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-zinc-300 hover:bg-white/[0.04] transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> New goal
            </Link>
            <Link
              to="/settings"
              onClick={() => setOpen(false)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-zinc-300 hover:bg-white/[0.04] transition-colors border-l border-white/5"
            >
              <SettingsIcon className="w-3.5 h-3.5" /> Manage
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
